/**
 * 积分项目选择器组件
 * 支持从项目池选择、显示常用项目、搜索过滤
 */
import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Input, Select, List, Tag, Button, Space, Row, Col, Badge, Empty, Spin, message, Typography } from 'antd';
import { SearchOutlined, StarOutlined, FireOutlined, PlusOutlined } from '@ant-design/icons';
import pointsTemplatesAPI from '../services/pointsTemplatesAPI';

const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;

/**
 * 积分项目选择器
 * @param {boolean} visible - 是否显示
 * @param {function} onClose - 关闭回调
 * @param {function} onSelect - 选择项目回调 (item) => void
 * @param {string} itemType - 类型：'add'|'deduct'|null (null表示全部)
 * @param {string} schoolStage - 学段：'小学'|'初中'|'高中'
 */
const PointsItemSelector = ({ visible, onClose, onSelect, itemType = null, schoolStage = '初中' }) => {
  const [activeTab, setActiveTab] = useState('frequent'); // frequent|pool|categories
  const [loading, setLoading] = useState(false);
  
  // 常用项目
  const [frequentItems, setFrequentItems] = useState([]);
  
  // 项目池数据
  const [poolItems, setPoolItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  
  // 分类数据
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // 搜索和筛选
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterType, setFilterType] = useState(itemType || 'all');
  const [filterFrequency, setFilterFrequency] = useState('all');

  useEffect(() => {
    if (visible) {
      loadFrequentItems();
      loadPoolItems();
      loadCategories();
    }
  }, [visible]);

  useEffect(() => {
    filterPoolItems();
  }, [poolItems, searchKeyword, filterType, filterFrequency, selectedCategory]);

  // 加载常用项目
  const loadFrequentItems = async () => {
    try {
      const response = await pointsTemplatesAPI.getFrequentlyUsed(10);
      if (response.data.success) {
        let items = response.data.data;
        if (itemType) {
          items = items.filter(item => item.item_type === itemType);
        }
        setFrequentItems(items);
      }
    } catch (error) {
      console.error('获取常用项目失败:', error);
    }
  };

  // 加载项目池
  const loadPoolItems = async () => {
    try {
      setLoading(true);
      const response = await pointsTemplatesAPI.getItemPool({
        school_stage: schoolStage,
        item_type: itemType
      });
      if (response.data.success) {
        setPoolItems(response.data.data);
      }
    } catch (error) {
      console.error('获取项目池失败:', error);
      message.error('获取项目池失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载分类
  const loadCategories = async () => {
    try {
      const response = await pointsTemplatesAPI.getCategories(itemType);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  // 过滤项目池
  const filterPoolItems = () => {
    let filtered = [...poolItems];

    // 搜索关键词
    if (searchKeyword) {
      filtered = filtered.filter(item =>
        item.item_name.includes(searchKeyword) ||
        (item.description && item.description.includes(searchKeyword))
      );
    }

    // 类型过滤
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.item_type === filterType);
    }

    // 频率过滤
    if (filterFrequency !== 'all') {
      filtered = filtered.filter(item => item.frequency === filterFrequency);
    }

    // 分类过滤
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  };

  // 处理项目选择
  const handleSelectItem = (item) => {
    onSelect && onSelect({
      id: item.id || item.pool_item_id,
      name: item.item_name,
      type: item.item_type,
      category: item.category,
      points: item.points_value || item.default_points,
      pool_item_id: item.id,
      user_template_item_id: item.id
    });
    onClose && onClose();
  };

  // 渲染项目卡片
  const renderItemCard = (item, showUseCount = false) => {
    const isAdd = item.item_type === 'add';
    const points = item.points_value || item.default_points;
    
    return (
      <List.Item
        key={item.id}
        onClick={() => handleSelectItem(item)}
        style={{ 
          cursor: 'pointer',
          padding: '12px',
          borderRadius: '4px',
          transition: 'all 0.3s'
        }}
        className="item-hover"
      >
        <List.Item.Meta
          title={
            <Space>
              <Tag color={isAdd ? 'green' : 'red'}>
                {isAdd ? '加分' : '扣分'}
              </Tag>
              <span>{item.item_name}</span>
              {item.frequency === 'high' && (
                <Badge count={<FireOutlined style={{ color: '#ff4d4f' }} />} />
              )}
            </Space>
          }
          description={
            <Space direction="vertical" size={0}>
              <Text type="secondary">{item.category}</Text>
              {item.description && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.description}
                </Text>
              )}
              {showUseCount && item.use_count > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  已使用 {item.use_count} 次
                </Text>
              )}
            </Space>
          }
        />
        <div>
          <Tag color={isAdd ? 'green' : 'red'} style={{ fontSize: 16, padding: '4px 12px' }}>
            {isAdd ? '+' : ''}{points}分
          </Tag>
        </div>
      </List.Item>
    );
  };

  // 按分类分组显示
  const renderCategoryGroups = () => {
    const grouped = {};
    filteredItems.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });

    return Object.keys(grouped).map(category => (
      <div key={category} style={{ marginBottom: 24 }}>
        <h4>{category} ({grouped[category].length})</h4>
        <List
          grid={{ gutter: 16, column: 1 }}
          dataSource={grouped[category]}
          renderItem={item => renderItemCard(item)}
        />
      </div>
    ));
  };

  return (
    <Modal
      title="选择积分项目"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* 常用项目 */}
        <TabPane 
          tab={<span><StarOutlined /> 常用项目</span>} 
          key="frequent"
        >
          {frequentItems.length > 0 ? (
            <List
              grid={{ gutter: 16, column: 1 }}
              dataSource={frequentItems}
              renderItem={item => renderItemCard(item, true)}
            />
          ) : (
            <Empty description="暂无常用项目，使用后会自动记录" />
          )}
        </TabPane>

        {/* 项目池 */}
        <TabPane 
          tab={<span><SearchOutlined /> 项目池 ({poolItems.length})</span>} 
          key="pool"
        >
          {/* 搜索和筛选 */}
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Search
              placeholder="搜索项目名称"
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              allowClear
            />
            <Row gutter={8}>
              <Col span={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="类型"
                  value={filterType}
                  onChange={setFilterType}
                >
                  <Option value="all">全部类型</Option>
                  <Option value="add">加分</Option>
                  <Option value="deduct">扣分</Option>
                </Select>
              </Col>
              <Col span={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="使用频率"
                  value={filterFrequency}
                  onChange={setFilterFrequency}
                >
                  <Option value="all">全部频率</Option>
                  <Option value="high">高频</Option>
                  <Option value="medium">中频</Option>
                  <Option value="low">低频</Option>
                </Select>
              </Col>
              <Col span={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="分类"
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  allowClear
                >
                  {categories.map(cat => (
                    <Option key={cat.category} value={cat.category}>
                      {cat.category} ({cat.item_count})
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Space>

          {/* 项目列表 */}
          <Spin spinning={loading}>
            {filteredItems.length > 0 ? (
              <List
                grid={{ gutter: 16, column: 1 }}
                dataSource={filteredItems}
                renderItem={item => renderItemCard(item)}
              />
            ) : (
              <Empty description="没有找到符合条件的项目" />
            )}
          </Spin>
        </TabPane>

        {/* 分类浏览 */}
        <TabPane 
          tab={<span>分类浏览</span>} 
          key="categories"
        >
          {loading ? (
            <Spin />
          ) : (
            <>
              <Select
                style={{ width: '100%', marginBottom: 16 }}
                placeholder="请先选择分类"
                value={selectedCategory}
                onChange={setSelectedCategory}
                allowClear
                size="large"
              >
                {categories.map(cat => (
                  <Option key={cat.category} value={cat.category}>
                    {cat.category} ({cat.item_count})
                  </Option>
                ))}
              </Select>
              
              {selectedCategory ? (
                <List
                  grid={{ gutter: 16, column: 1 }}
                  dataSource={filteredItems}
                  renderItem={item => renderItemCard(item)}
                  locale={{ emptyText: '该分类下暂无项目' }}
                />
              ) : (
                <Empty 
                  description="请先选择一个分类查看具体项目"
                  style={{ padding: '40px 0' }}
                />
              )}
            </>
          )}
        </TabPane>
      </Tabs>

      <style jsx>{`
        .item-hover:hover {
          background-color: #f0f0f0;
        }
      `}</style>
    </Modal>
  );
};

export default PointsItemSelector;

